import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  FormLabel,
  FormControlLabel,
  IconButton,
  Tooltip,
  Grid,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Badge
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import { useTheme } from "@mui/material";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Custom Component Imports
import CustomTextField from "../../../components/mui/text-field";
import CustomChip from '../../../components/mui/chip';
import CustomAutocomplete from "components/mui/autocomplete";

// ** Third Party Imports
import isEqual from "lodash/isEqual";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      width: 250,
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const tagList = ["Senior", "Junior", "Intern", "On-call"];

const AdvancedFilter = ( {onApplyFilters, handleReset, filtered} ) => {
  const theme = useTheme();

  // ** States
  const [openCusInfo, setOpenCusInfo] = useState(false);
  const [openJobsiteNPropInfo, setOpenJobsiteNPropInfo] = useState(false);
  const [openServiceHistory, setOpenServiceHistory] = useState(false);
  const [openTagNStatus, setOpenTagNStatus] = useState(false);
  const [tags, setTags] = useState([]);
  const [open, setOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [recordTypeOpen, setRecordTypeOpen] = useState(false);

  const handleTagChange = (event) => {
    const value = event.target.value;
    setFilters({ ...filters, tags: (typeof value === "string" ? value.split(",") : value) })
    setTags(typeof value === "string" ? value.split(",") : value);
  };

  const initialFilterState = {
    fullName: '',
    customerType: 'select',
    email: '',
    phoneNumber: '',
    companyName: '',
    status: 'select',
    tags: [],
    address: '',
    billing_address: '',
    jobsite_name: '',
    jobsite_type: 'select',
    property_areaname: '',
    property_areatype: 'select',
  }
  const [filters, setFilters] = useState(initialFilterState);

  const handleApply = () => {
    const sanitizedFilters = {
      ...filters,
      customerType: filters.customerType === "select" ? "" : filters.customerType,
      status: filters.status === "select" ? "" : filters.status,
      jobsite_type: filters.jobsite_type === "select" ? "" : filters.jobsite_type,
      property_areatype: filters.property_areatype === "select" ? "" : filters.property_areatype,
    };
    onApplyFilters(sanitizedFilters); // Pass the filters to the parent
    setOpen(false)
  };

  const resetFilter = (popupClose) => {
    handleReset();
    if(popupClose == false){

    } else {
      setOpen(false);
    }

    setFilters({
      fullName: '',
      customerType: 'select',
      email: '',
      phoneNumber: '',
      companyName: '',
      status: 'select',
      tags: [],
      address: '',
      billing_address: '',
      jobsite_name: '',
      jobsite_type: 'select',
      property_areaname: '',
      property_areatype: 'select',
    });
    setTags([]);
  }

  const sections = {
    customerInfo: ["fullName", "customerType", "phoneNumber", "email", "address", "companyName", "billing_address"],
    jobsiteInfo: ["jobsite_name", "jobsite_type", "property_areaname", "property_areatype"],
    serviceInfo: [""],
    tagsAndStatus: ["status", "tags"]
  };
  const getSelectedCount = (sectionKeys) => {
    return sectionKeys.filter((key) => {
      const value = filters[key];
      // Ignore falsy values, "select", and empty arrays
      if (Array.isArray(value)) {
        return value.length > 0; // Count only non-empty arrays
      }
      return value && value !== "select";
    }).length;
  };  

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* <Box> */}
        {isEqual(initialFilterState, filters) ? (
          <Button variant='outlined' color={theme.palette.mode === 'dark' ? 'secondary' : 'primary'} onClick={()=> setOpen(true)} startIcon={<Icon icon='tabler:filter' />} 
            sx={{
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Filter
          </Button>
        ) : (
          <Badge 
            color='error' 
            sx={{ cursor: 'pointer'}}
            badgeContent={
            <Typography variant='body2' sx={{ color:'white', lineHeight: 1 }} 
              onClick={() => {
                const popupClose = true;
                resetFilter(popupClose);
              }
            }
              overlap='circular'
            >
              X
            </Typography>
            } 
          >
            <Button variant='outlined' color={theme.palette.mode === 'dark' ? 'secondary' : 'primary'} onClick={()=> setOpen(true)} startIcon={<Icon icon='tabler:filter' />}
              sx={{
                width: { xs: '100%', sm: 'auto' }
              }}
            >Filter</Button>
          </Badge>
        )}
        <Dialog
          fullWidth
          maxWidth="sm"
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "20px 24px",
            }}
          >
            <Typography variant="h3">Advanced Filter</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">
                {filtered ? filtered.length : '0'} customers match your criteria
              </Typography>
              <Tooltip title='You don’t need to input exact details. A part of the name, address, or phone number is enough to proceed.'>
                <IconButton color='info'>
                  <Icon icon='tabler:info-circle' />
                </IconButton>
              </Tooltip>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton onClick={() => setOpenCusInfo(!openCusInfo)}>
                    <Icon
                      icon={
                        openCusInfo
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Customer Information
                          </Typography>
                          <CustomChip 
                            label={getSelectedCount(sections.customerInfo)}
                            skin='light'
                            color='success'
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openCusInfo} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Full Name"
                        placeholder="John Doe"
                        value={filters.fullName}
                        onChange={(e) => setFilters({ ...filters, fullName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Company Name"
                        placeholder="Cleri"
                        value={filters.companyName}
                        onChange={(e) => setFilters({ ...filters, companyName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Customer Type"
                        defaultValue="select"
                        value={filters.customerType}
                        onChange={(e) => setFilters({ ...filters, customerType: e.target.value })}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                        <MenuItem value="Individual">
                          Individual
                        </MenuItem>
                        <MenuItem value="Business" >
                          Business
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Phone Number"
                        placeholder="000 000 0000"
                        value={filters.phoneNumber}
                        onChange={(e) => setFilters({ ...filters, phoneNumber: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Email"
                        placeholder="john@example.com"
                        value={filters.email}
                        onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Address"
                        placeholder="123 street, main road"
                        value={filters.address}
                        onChange={(e) => setFilters({ ...filters, address: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Billing Address"
                        placeholder="123 street, main road"
                        value={filters.billing_address}
                        onChange={(e) => setFilters({ ...filters, billing_address: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() =>
                      setOpenJobsiteNPropInfo(!openJobsiteNPropInfo)
                    }
                  >
                    <Icon
                      icon={
                        openJobsiteNPropInfo
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Jobsite and Property Information
                          </Typography>
                          <CustomChip 
                            label={getSelectedCount(sections.jobsiteInfo)}
                            skin='light'
                            color='success'
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse
                  in={openJobsiteNPropInfo}
                  timeout="auto"
                  unmountOnExit
                >
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Jobsite Name"
                        placeholder="Warehouse"
                        value={filters.jobsite_name}
                        onChange={(e) => setFilters({ ...filters, jobsite_name: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Jobsite Type"
                        defaultValue="select"
                        value={filters.jobsite_type}
                        onChange={(e) => setFilters({ ...filters, jobsite_type: e.target.value })}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        label="Property Area Name"
                        placeholder="Side Yard, Back Yard"
                        value={filters.property_areaname}
                        onChange={(e) => setFilters({ ...filters, property_areaname: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Property Area Type"
                        defaultValue="select"
                        value={filters.property_areatype}
                        onChange={(e) => setFilters({ ...filters, property_areatype: e.target.value })}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                      </CustomTextField>
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() => setOpenServiceHistory(!openServiceHistory)}
                  >
                    <Icon
                      icon={
                        openServiceHistory
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Service History
                          </Typography>
                          <CustomChip 
                            label={getSelectedCount(sections.serviceInfo)}
                            skin='light'
                            color='success'
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openServiceHistory} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12}>
                    <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            marginBottom: "4px",
                            color: "text.primary",
                            height: "100%",
                          }}
                        >
                          Last Service Date
                        </FormLabel>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 5
                          }}
                        >
                          <DatePicker
                            label="From"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                          <DatePicker
                            label="To"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomAutocomplete
                        open={recordTypeOpen}
                        onClose={() => setRecordTypeOpen(false)}
                        options={[]}
                        id='autocomplete-custom'
                        getOptionLabel={(option) => option || ''}
                        renderInput={
                          params => 
                            <CustomTextField 
                              {...params} 
                              fullWidth 
                              label="Record Type"
                              placeholder='Select' 
                              onClick={
                                () => setRecordTypeOpen(!recordTypeOpen)
                              }
                            />
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomAutocomplete
                        open={serviceOpen}
                        onClose={() => setServiceOpen(false)}
                        options={[]}
                        id='autocomplete-custom'
                        getOptionLabel={(option) => option || ''}
                        renderInput={
                          params => 
                            <CustomTextField 
                              {...params} 
                              fullWidth 
                              label="Services"
                              placeholder='Select' 
                              onClick={
                                () => setServiceOpen(!serviceOpen)
                              }
                            />
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            marginBottom: "4px",
                            color: "text.primary",
                            height: "100%",
                          }}
                        >
                          Total Revenue
                        </FormLabel>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 5
                          }}
                        >
                          <CustomTextField
                            fullWidth
                            placeholder="Min Amount"
                          />
                          <CustomTextField
                            fullWidth
                            placeholder="Max Amount"
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Collapse>
              </List>
              <List component="nav" aria-label="main mailbox">
                <ListItem
                  disablePadding
                  sx={{ borderBottom: "1px solid #E1E4EA" }}
                >
                  <ListItemButton
                    onClick={() => setOpenTagNStatus(!openTagNStatus)}
                  >
                    <Icon
                      icon={
                        openTagNStatus
                          ? "tabler:chevron-down"
                          : "tabler:chevron-right"
                      }
                    />
                    <ListItemText
                      sx={{ ml: 4 }}
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Tags and Status
                          </Typography>
                          <CustomChip 
                            label={getSelectedCount(sections.tagsAndStatus)}
                            skin='light'
                            color='success'
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={openTagNStatus} timeout="auto" unmountOnExit>
                  <Grid container spacing={5} py={4} px={4}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Status"
                        defaultValue="select"
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                        <MenuItem value={true}>Active</MenuItem>
                        <MenuItem value={false}>Inactive</MenuItem>
                      </CustomTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
  <CustomTextField
    fullWidth
    label="Tags"
    placeholder="e.g., On-call, Senior, Junior"
    value={tags.join(", ")}
    onChange={(e) => handleTagChange(e)}
  />
</Grid>
                  </Grid>
                </Collapse>
              </List>
            </Box>
          </DialogContent>
          <Divider />
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "20px 24px !important",
            }}
            className="dialog-actions-dense"
          >
            <Box>
              <Button
                variant="contained"
                color="info"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 4,
              }}
            >
              <Button variant="tonal" color="primary"
                onClick={() => {
                  const popupClose = false;
                  resetFilter(popupClose);
                }
              }
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="main"
                onClick={handleApply}
              >
                Apply
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      {/* </Box> */}
    </LocalizationProvider>
  );
};

export default AdvancedFilter;
